import React from 'react'
import * as stores from '../stores/appStore'
import GroupModal from './GroupModal'

const Settings: React.FC = () => {
  const { people, groups, exportData, importData, clearAllData, deleteGroup } = stores.useAppStore()
  const [activeTab, setActiveTab] = React.useState<'general' | 'data' | 'groups' | 'about'>('general')
  const [isGroupModalOpen, setIsGroupModalOpen] = React.useState(false)
  const [editingGroup, setEditingGroup] = React.useState<any>(undefined)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const handleExport = async () => {
    try {
      const data = await exportData()
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `bookeveryone-backup-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export data:', error)
      alert('Failed to export data')
    }
  }

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      const text = await file.text()
      const data = JSON.parse(text)

      if (confirm('This will replace all existing data. Are you sure?')) {
        await importData(data)
        alert('Data imported successfully!')
      }
    } catch (error) {
      console.error('Failed to import data:', error)
      alert('Failed to import data. Please check the file format.')
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClearAll = async () => {
    if (confirm('This will permanently delete all people, calendars, and settings. Are you sure?')) {
      if (confirm('This action cannot be undone. Are you absolutely sure?')) {
        await clearAllData()
        alert('All data has been cleared')
      }
    }
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <nav className="flex space-x-8 px-6">
          {[
            { id: 'general', label: 'General' },
            { id: 'data', label: 'Data Management' },
            { id: 'groups', label: 'Groups' },
            { id: 'about', label: 'About' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'general' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">General Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <p className="font-medium">Auto-refresh</p>
                    <p className="text-sm text-gray-500">Automatically sync calendars periodically</p>
                  </div>
                  <button className="text-sm text-gray-500">Coming soon</button>
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <p className="font-medium">Theme</p>
                    <p className="text-sm text-gray-500">Choose your preferred color theme</p>
                  </div>
                  <button className="text-sm text-gray-500">Coming soon</button>
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <p className="font-medium">Time format</p>
                    <p className="text-sm text-gray-500">12-hour or 24-hour format</p>
                  </div>
                  <button className="text-sm text-gray-500">Coming soon</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'data' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Data Management</h3>

              {/* Export Section */}
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-medium text-green-900 mb-2">Export Data</h4>
                <p className="text-sm text-green-700 mb-3">
                  Download all your people, calendars, and settings as a JSON file
                </p>
                <button
                  onClick={handleExport}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Export Data
                </button>
              </div>

              {/* Import Section */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2">Import Data</h4>
                <p className="text-sm text-blue-700 mb-3">
                  Restore from a previously exported JSON file
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Import Data
                </button>
              </div>

              {/* Clear Data Section */}
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <h4 className="font-medium text-red-900 mb-2">Clear All Data</h4>
                <p className="text-sm text-red-700 mb-3">
                  Permanently delete all people, calendars, and settings
                </p>
                <button
                  onClick={handleClearAll}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Clear All Data
                </button>
              </div>
            </div>

            {/* Statistics */}
            <div className="pt-6 border-t">
              <h4 className="font-medium mb-4">Statistics</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{people.length}</p>
                  <p className="text-sm text-gray-600">People</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">
                    {people.reduce((acc, p) => acc + p.calendars.length, 0)}
                  </p>
                  <p className="text-sm text-gray-600">Calendars</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{groups.length}</p>
                  <p className="text-sm text-gray-600">Groups</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'groups' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Groups</h3>
              <p className="text-gray-500 mb-6">Organize people into groups for easier management</p>

              {groups.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 mb-4">No groups created yet</p>
                  <button
                    onClick={() => {
                      setEditingGroup(undefined)
                      setIsGroupModalOpen(true)
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Create First Group
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {groups.map(group => (
                    <div key={group.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{group.emoji}</span>
                        <div>
                          <p className="font-medium">{group.name}</p>
                          <p className="text-sm text-gray-500">
                            {group.memberIds.length} member{group.memberIds.length !== 1 && 's'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setEditingGroup(group)
                            setIsGroupModalOpen(true)
                          }}
                          className="p-1 text-gray-600 hover:text-primary-600"
                          title="Edit Group"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Delete group "${group.name}"?`)) {
                              deleteGroup(group.id)
                            }
                          }}
                          className="p-1 text-gray-600 hover:text-red-600"
                          title="Delete Group"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      setEditingGroup(undefined)
                      setIsGroupModalOpen(true)
                    }}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
                  >
                    + Create New Group
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'about' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">About BookEveryone</h3>
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-600 mb-4">
                  BookEveryone is a local-first calendar aggregation tool that helps you manage
                  multiple people's availability in one beautiful interface.
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-blue-900 mb-2">Privacy First</h4>
                  <p className="text-sm text-blue-700">
                    All your data is stored locally in your browser. We never send your data to any servers,
                    and you maintain complete control over your information.
                  </p>
                </div>

                <div className="space-y-2 mb-4">
                  <h4 className="font-medium">Features:</h4>
                  <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
                    <li>Aggregate multiple Calendly links in one view</li>
                    <li>View availability in calendar or list format</li>
                    <li>Click to book directly on Calendly</li>
                    <li>Export and import your data</li>
                    <li>Organize people into groups</li>
                    <li>Beautiful, responsive interface</li>
                  </ul>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500">
                    Version 1.0.0 • Built with React, TypeScript, and IndexedDB
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    <a href="https://github.com/jlumbroso/bookeveryone-ui" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">
                      View on GitHub
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Group Modal */}
      {isGroupModalOpen && (
        <GroupModal
          isOpen={isGroupModalOpen}
          onClose={() => {
            setIsGroupModalOpen(false)
            setEditingGroup(undefined)
          }}
          group={editingGroup}
        />
      )}
    </div>
  )
}

export default Settings